import { Formula } from "../types";

export const IGCSE_FORMULAS: Formula[] = [
  // UNIT 1: Motion, Forces & Energy
  {
    id: "speed",
    name: "Average Speed",
    equation: "v = s / t",
    topicId: "unit-1",
    variables: [
      { symbol: "v", name: "Speed (or Velocity)", unit: "m/s" },
      { symbol: "s", name: "Distance (or Displacement)", unit: "m" },
      { symbol: "t", name: "Time", unit: "s" },
    ],
    triangle: ["s", "v", "t"],
    notes: "For constant speed or average speed. Remember 1 km/h = (1000/3600) m/s = 0.278 m/s.",
  },
  {
    id: "acceleration",
    name: "Acceleration",
    equation: "a = (v - u) / t",
    topicId: "unit-1",
    variables: [
      { symbol: "a", name: "Acceleration", unit: "m/s²" },
      { symbol: "v", name: "Final velocity", unit: "m/s" },
      { symbol: "u", name: "Initial velocity", unit: "m/s" },
      { symbol: "t", name: "Time taken", unit: "s" },
    ],
    notes: "Deceleration/retardation is negative acceleration. Free fall acceleration on Earth g ≈ 9.8 m/s² (or 10 m/s² for IGCSE papers).",
  },
  {
    id: "density",
    name: "Density",
    equation: "ρ = m / V",
    topicId: "unit-1",
    variables: [
      { symbol: "ρ", name: "Density (rho)", unit: "kg/m³ or g/cm³" },
      { symbol: "m", name: "Mass", unit: "kg or g" },
      { symbol: "V", name: "Volume", unit: "m³ or cm³" },
    ],
    triangle: ["m", "ρ", "V"],
    notes: "Conversion: 1 g/cm³ = 1000 kg/m³. Water density is 1.0 g/cm³ or 1000 kg/m³.",
  },
  {
    id: "weight",
    name: "Weight & Gravitational Force",
    equation: "W = m · g",
    topicId: "unit-1",
    variables: [
      { symbol: "W", name: "Weight", unit: "N (Newtons)" },
      { symbol: "m", name: "Mass", unit: "kg" },
      { symbol: "g", name: "Gravitational field strength", unit: "N/kg (or m/s²)" },
    ],
    triangle: ["W", "m", "g"],
    notes: "Mass never changes with location, but Weight varies depending on g. On Earth g = 9.8 N/kg (often 10 N/kg in CIE).",
  },
  {
    id: "newton2",
    name: "Resultant Force (Newton's 2nd Law)",
    equation: "F = m · a",
    topicId: "unit-1",
    variables: [
      { symbol: "F", name: "Resultant Force", unit: "N" },
      { symbol: "m", name: "Mass", unit: "kg" },
      { symbol: "a", name: "Acceleration", unit: "m/s²" },
    ],
    triangle: ["F", "m", "a"],
    notes: "F must be the RESULTANT (unbalanced) force. If forces are balanced, resultant F = 0, so acceleration a = 0 (constant velocity).",
  },
  {
    id: "hooke",
    name: "Hooke's Law",
    equation: "F = k · x",
    topicId: "unit-1",
    variables: [
      { symbol: "F", name: "Load / Applied Force", unit: "N" },
      { symbol: "k", name: "Spring Constant (Stiffness)", unit: "N/m or N/cm" },
      { symbol: "x", name: "Extension (Length - Initial Length)", unit: "m or cm" },
    ],
    triangle: ["F", "k", "x"],
    notes: "Valid only up to the 'Limit of Proportionality'. Extension x = (Stretched length - Original length).",
  },
  {
    id: "moment",
    name: "Moment of a Force",
    equation: "Moment = F · d",
    topicId: "unit-1",
    variables: [
      { symbol: "Moment", name: "Turning effect", unit: "N·m or N·cm" },
      { symbol: "F", name: "Force", unit: "N" },
      { symbol: "d", name: "Perpendicular distance to pivot", unit: "m or cm" },
    ],
    notes: "For an object in equilibrium: Total Clockwise Moments = Total Anticlockwise Moments & Resultant force is zero.",
  },
  {
    id: "momentum",
    name: "Momentum",
    equation: "p = m · v",
    topicId: "unit-1",
    variables: [
      { symbol: "p", name: "Momentum", unit: "kg·m/s" },
      { symbol: "m", name: "Mass", unit: "kg" },
      { symbol: "v", name: "Velocity", unit: "m/s" },
    ],
    triangle: ["p", "m", "v"],
    notes: "Momentum is a VECTOR quantity. Total momentum before collision = Total momentum after collision (in a closed system).",
  },
  {
    id: "impulse",
    name: "Impulse and Force",
    equation: "Impulse = F · Δt = Δp = m·v - m·u",
    topicId: "unit-1",
    variables: [
      { symbol: "Impulse", name: "Impulse of force", unit: "N·s (or kg·m/s)" },
      { symbol: "F", name: "Resultant Force", unit: "N" },
      { symbol: "Δt", name: "Time interval", unit: "s" },
      { symbol: "Δp", name: "Change in momentum", unit: "kg·m/s" },
    ],
    notes: "Seatbelts and crumple zones increase impact time Δt, thereby reducing the impact force F for the same change in momentum.",
  },
  {
    id: "work",
    name: "Work Done",
    equation: "W = F · d",
    topicId: "unit-1",
    variables: [
      { symbol: "W", name: "Work Done (Energy Transferred)", unit: "J (Joules)" },
      { symbol: "F", name: "Force in direction of motion", unit: "N" },
      { symbol: "d", name: "Distance moved in force direction", unit: "m" },
    ],
    triangle: ["W", "F", "d"],
    notes: "1 Joule = 1 Newton × 1 meter (1 J = 1 N·m).",
  },
  {
    id: "ke",
    name: "Kinetic Energy",
    equation: "KE = ½ · m · v²",
    topicId: "unit-1",
    variables: [
      { symbol: "KE", name: "Kinetic Energy", unit: "J" },
      { symbol: "m", name: "Mass", unit: "kg" },
      { symbol: "v", name: "Speed", unit: "m/s" },
    ],
    notes: "If speed doubles, kinetic energy quadruples (2² = 4).",
  },
  {
    id: "gpe",
    name: "Gravitational Potential Energy",
    equation: "ΔGPE = m · g · Δh",
    topicId: "unit-1",
    variables: [
      { symbol: "ΔGPE", name: "Change in GPE", unit: "J" },
      { symbol: "m", name: "Mass", unit: "kg" },
      { symbol: "g", name: "Gravitational field strength", unit: "N/kg" },
      { symbol: "Δh", name: "Vertical height change", unit: "m" },
    ],
    notes: "For an object falling without air resistance: Loss in GPE = Gain in KE.",
  },
  {
    id: "power",
    name: "Power",
    equation: "P = W / t = ΔE / t",
    topicId: "unit-1",
    variables: [
      { symbol: "P", name: "Power (rate of energy transfer)", unit: "W (Watts = J/s)" },
      { symbol: "W", name: "Work done or Energy transferred", unit: "J" },
      { symbol: "t", name: "Time taken", unit: "s" },
    ],
    triangle: ["W", "P", "t"],
    notes: "1 Watt is 1 Joule of energy transferred per second (1 W = 1 J/s).",
  },
  {
    id: "efficiency",
    name: "Efficiency",
    equation: "Efficiency = (Useful Energy Output / Total Energy Input) × 100%",
    topicId: "unit-1",
    variables: [
      { symbol: "Eff", name: "Efficiency", unit: "% (or decimal < 1)" },
      { symbol: "E_useful", name: "Useful energy output", unit: "J or W" },
      { symbol: "E_total", name: "Total energy input", unit: "J or W" },
    ],
    notes: "Efficiency can never exceed 100% due to energy conservation and dissipation as heat/sound.",
  },
  {
    id: "pressure",
    name: "Solid Surface Pressure",
    equation: "p = F / A",
    topicId: "unit-1",
    variables: [
      { symbol: "p", name: "Pressure", unit: "Pa (Pascals) or N/m²" },
      { symbol: "F", name: "Normal Force", unit: "N" },
      { symbol: "A", name: "Surface Area", unit: "m² (or cm²)" },
    ],
    triangle: ["F", "p", "A"],
    notes: "1 Pa = 1 N/m². 1 cm² = 0.0001 m² (10⁻⁴ m²). Smaller contact area generates higher pressure (e.g. sharp knife, needle).",
  },
  {
    id: "fluid_pressure",
    name: "Liquid Pressure (Hydrostatic)",
    equation: "Δp = ρ · g · h",
    topicId: "unit-1",
    variables: [
      { symbol: "Δp", name: "Pressure in fluid", unit: "Pa" },
      { symbol: "ρ", name: "Fluid density", unit: "kg/m³" },
      { symbol: "g", name: "Gravitational field strength", unit: "N/kg" },
      { symbol: "h", name: "Depth below liquid surface", unit: "m" },
    ],
    notes: "Total pressure at depth h = Atmospheric pressure (p_atm) + ρgh. Liquid pressure acts equally in all directions.",
  },

  // UNIT 2: Thermal Physics
  {
    id: "shc",
    name: "Specific Heat Capacity",
    equation: "Q = m · c · ΔT",
    topicId: "unit-2",
    variables: [
      { symbol: "Q", name: "Thermal Energy Supplied", unit: "J" },
      { symbol: "m", name: "Mass", unit: "kg" },
      { symbol: "c", name: "Specific Heat Capacity", unit: "J / (kg·°C)" },
      { symbol: "ΔT", name: "Temperature change (T2 - T1)", unit: "°C or K" },
    ],
    notes: "Water has a very high c (≈ 4200 J/kg°C), making it excellent for coolants and radiators.",
  },
  {
    id: "latent_heat",
    name: "Specific Latent Heat",
    equation: "Q = m · L",
    topicId: "unit-2",
    variables: [
      { symbol: "Q", name: "Energy during state change", unit: "J" },
      { symbol: "m", name: "Mass of substance changing state", unit: "kg" },
      { symbol: "L", name: "Specific Latent Heat (Fusion L_f or Vaporization L_v)", unit: "J/kg" },
    ],
    triangle: ["Q", "m", "L"],
    notes: "Temperature remains constant during melting, freezing, boiling, or condensing because energy is breaking/forming bonds.",
  },
  {
    id: "boyle_gas",
    name: "Boyle's Law (Gas Pressure & Volume)",
    equation: "p₁ · V₁ = p₂ · V₂ (at constant temperature)",
    topicId: "unit-2",
    variables: [
      { symbol: "p₁", name: "Initial Pressure", unit: "Pa" },
      { symbol: "V₁", name: "Initial Volume", unit: "m³ or cm³" },
      { symbol: "p₂", name: "Final Pressure", unit: "Pa" },
      { symbol: "V₂", name: "Final Volume", unit: "m³ or cm³" },
    ],
    notes: "p is inversely proportional to V (p ∝ 1/V) when temperature and mass of gas are kept constant.",
  },

  // UNIT 3: Waves & Light
  {
    id: "wave_speed",
    name: "The Wave Equation",
    equation: "v = f · λ",
    topicId: "unit-3",
    variables: [
      { symbol: "v", name: "Wave speed", unit: "m/s" },
      { symbol: "f", name: "Frequency (waves per second)", unit: "Hz (Hertz)" },
      { symbol: "λ", name: "Wavelength (lambda)", unit: "m" },
    ],
    triangle: ["v", "f", "λ"],
    notes: "Frequency f = 1 / T (where T is time period in seconds). Wave speed in vacuum for all EM waves c = 3 × 10⁸ m/s.",
  },
  {
    id: "period_freq",
    name: "Period & Frequency",
    equation: "f = 1 / T",
    topicId: "unit-3",
    variables: [
      { symbol: "f", name: "Frequency", unit: "Hz" },
      { symbol: "T", name: "Time period of one full cycle", unit: "s" },
    ],
    triangle: ["1", "f", "T"],
    notes: "Period is the time taken for one complete wave oscillation to pass a point.",
  },
  {
    id: "refractive_index_angles",
    name: "Snell's Law (Refractive Index)",
    equation: "n = sin(i) / sin(r)",
    topicId: "unit-3",
    variables: [
      { symbol: "n", name: "Refractive Index (dimensionless)", unit: "no units (> 1.0)" },
      { symbol: "i", name: "Angle of incidence in air / less dense medium", unit: "degrees (°)" },
      { symbol: "r", name: "Angle of refraction in glass / denser medium", unit: "degrees (°)" },
    ],
    notes: "Angles MUST always be measured between the ray and the NORMAL line (90° to boundary). Glass n ≈ 1.5, Water n ≈ 1.33.",
  },
  {
    id: "refractive_index_speeds",
    name: "Refractive Index from Speeds",
    equation: "n = c / v",
    topicId: "unit-3",
    variables: [
      { symbol: "n", name: "Refractive Index", unit: "no units" },
      { symbol: "c", name: "Speed of light in vacuum", unit: "3.0 × 10⁸ m/s" },
      { symbol: "v", name: "Speed of light in medium", unit: "m/s" },
    ],
    notes: "Light slows down when entering an optically denser medium, bending towards the normal.",
  },
  {
    id: "critical_angle",
    name: "Critical Angle & Total Internal Reflection",
    equation: "sin(c) = 1 / n",
    topicId: "unit-3",
    variables: [
      { symbol: "c", name: "Critical angle", unit: "degrees (°)" },
      { symbol: "n", name: "Refractive index of dense medium", unit: "no units" },
    ],
    notes: "Total Internal Reflection (TIR) occurs ONLY when: 1) Light travels from denser to less dense medium; 2) Angle of incidence i > Critical angle c.",
  },
  {
    id: "echo_speed",
    name: "Echo & Sonar Distance",
    equation: "d = (v · t) / 2",
    topicId: "unit-3",
    variables: [
      { symbol: "d", name: "Distance to reflecting obstacle", unit: "m" },
      { symbol: "v", name: "Speed of sound", unit: "m/s (≈ 330–340 m/s in air)" },
      { symbol: "t", name: "Total time for echo round-trip", unit: "s" },
    ],
    notes: "Remember to divide by 2 because the sound wave travels to the barrier and back!",
  },

  // UNIT 4: Electricity & Magnetism
  {
    id: "charge_current",
    name: "Electric Charge",
    equation: "Q = I · t",
    topicId: "unit-4",
    variables: [
      { symbol: "Q", name: "Charge", unit: "C (Coulombs)" },
      { symbol: "I", name: "Current", unit: "A (Amperes)" },
      { symbol: "t", name: "Time", unit: "s" },
    ],
    triangle: ["Q", "I", "t"],
    notes: "Current is the rate of flow of electric charge (1 A = 1 C/s).",
  },
  {
    id: "ohms_law",
    name: "Ohm's Law & Resistance",
    equation: "V = I · R",
    topicId: "unit-4",
    variables: [
      { symbol: "V", name: "Potential Difference (Voltage)", unit: "V (Volts)" },
      { symbol: "I", name: "Current", unit: "A (Amperes)" },
      { symbol: "R", name: "Resistance", unit: "Ω (Ohms)" },
    ],
    triangle: ["V", "I", "R"],
    notes: "For an Ohmic conductor at constant temperature, current is directly proportional to voltage (straight line graph through origin).",
  },
  {
    id: "voltage_energy",
    name: "Electromotive Force / Potential Difference",
    equation: "V = W / Q = E / Q",
    topicId: "unit-4",
    variables: [
      { symbol: "V", name: "Voltage (EMF or p.d.)", unit: "V" },
      { symbol: "W", name: "Energy transferred", unit: "J" },
      { symbol: "Q", name: "Charge passing through", unit: "C" },
    ],
    triangle: ["W", "V", "Q"],
    notes: "1 Volt is 1 Joule per Coulomb (1 V = 1 J/C). EMF is energy given per unit charge by source; p.d. is energy dissipated per unit charge in a component.",
  },
  {
    id: "series_resistors",
    name: "Resistors in Series",
    equation: "R_total = R₁ + R₂ + R₃ + ...",
    topicId: "unit-4",
    variables: [
      { symbol: "R_total", name: "Total equivalent resistance", unit: "Ω" },
      { symbol: "R₁", name: "First resistor", unit: "Ω" },
      { symbol: "R₂", name: "Second resistor", unit: "Ω" },
    ],
    notes: "In series: Current is the SAME through all components; Total voltage splits across resistors V_total = V1 + V2.",
  },
  {
    id: "parallel_resistors",
    name: "Resistors in Parallel",
    equation: "1 / R_total = 1 / R₁ + 1 / R₂",
    topicId: "unit-4",
    variables: [
      { symbol: "R_total", name: "Total equivalent resistance", unit: "Ω" },
      { symbol: "R₁", name: "Resistor 1", unit: "Ω" },
      { symbol: "R₂", name: "Resistor 2", unit: "Ω" },
    ],
    notes: "Shortcut for two resistors in parallel: R_total = (R₁ × R₂) / (R₁ + R₂). Parallel resistance is always LESS than the smallest single resistor.",
  },
  {
    id: "potential_divider",
    name: "Potential Divider (Voltage Divider)",
    equation: "V₁ = (R₁ / (R₁ + R₂)) · V_in",
    topicId: "unit-4",
    variables: [
      { symbol: "V₁", name: "Output voltage across R₁", unit: "V" },
      { symbol: "R₁", name: "Resistance 1", unit: "Ω" },
      { symbol: "R₂", name: "Resistance 2", unit: "Ω" },
      { symbol: "V_in", name: "Supply input voltage", unit: "V" },
    ],
    notes: "V₁ / V₂ = R₁ / R₂. Used with LDRs (light sensors) and Thermistors (temperature sensors) to trigger switching circuits.",
  },
  {
    id: "electric_power",
    name: "Electrical Power",
    equation: "P = I · V = I² · R = V² / R",
    topicId: "unit-4",
    variables: [
      { symbol: "P", name: "Power", unit: "W" },
      { symbol: "I", name: "Current", unit: "A" },
      { symbol: "V", name: "Voltage", unit: "V" },
      { symbol: "R", name: "Resistance", unit: "Ω" },
    ],
    triangle: ["P", "I", "V"],
    notes: "Use P = I²R to calculate power lost as heat in transmission cables (reducing current I by stepping up voltage minimizes power loss).",
  },
  {
    id: "electric_energy",
    name: "Electrical Energy",
    equation: "E = P · t = I · V · t",
    topicId: "unit-4",
    variables: [
      { symbol: "E", name: "Electrical Energy", unit: "J (or kWh)" },
      { symbol: "I", name: "Current", unit: "A" },
      { symbol: "V", name: "Voltage", unit: "V" },
      { symbol: "t", name: "Time", unit: "s (or hours for kWh)" },
    ],
    notes: "In domestic billing: 1 kilowatt-hour (1 kWh) = 1 kW × 1 hour = 1000 W × 3600 s = 3,600,000 J (3.6 MJ).",
  },
  {
    id: "transformer_turns",
    name: "Transformer Voltage & Turns Ratio",
    equation: "V_p / V_s = N_p / N_s",
    topicId: "unit-4",
    variables: [
      { symbol: "V_p", name: "Primary coil voltage (input)", unit: "V" },
      { symbol: "V_s", name: "Secondary coil voltage (output)", unit: "V" },
      { symbol: "N_p", name: "Number of primary turns", unit: "turns" },
      { symbol: "N_s", name: "Number of secondary turns", unit: "turns" },
    ],
    notes: "Step-up transformer: N_s > N_p (V_s > V_p). Step-down transformer: N_s < N_p (V_s < V_p). Only works with AC (Alternating Current).",
  },
  {
    id: "transformer_power",
    name: "100% Efficient Transformer (Power Balance)",
    equation: "I_p · V_p = I_s · V_s",
    topicId: "unit-4",
    variables: [
      { symbol: "I_p", name: "Primary current", unit: "A" },
      { symbol: "V_p", name: "Primary voltage", unit: "V" },
      { symbol: "I_s", name: "Secondary current", unit: "A" },
      { symbol: "V_s", name: "Secondary voltage", unit: "V" },
    ],
    notes: "If voltage is stepped up by 10×, current is stepped down by 10× to conserve energy.",
  },

  // UNIT 6: Space Physics
  {
    id: "orbital_speed",
    name: "Orbital Speed",
    equation: "v = (2 · π · r) / T",
    topicId: "unit-6",
    variables: [
      { symbol: "v", name: "Orbital speed", unit: "m/s or km/s" },
      { symbol: "r", name: "Average orbital radius", unit: "m or km" },
      { symbol: "T", name: "Orbital period (time for 1 full orbit)", unit: "s or days" },
    ],
    notes: "2πr is the circumference of the circular orbit. Planets closer to the Sun have smaller radii and travel at higher orbital speeds.",
  },
  {
    id: "hubble_law",
    name: "Hubble's Law (Expansion of Universe)",
    equation: "v = H₀ · d",
    topicId: "unit-6",
    variables: [
      { symbol: "v", name: "Recession velocity of distant galaxy", unit: "km/s" },
      { symbol: "H₀", name: "Hubble constant", unit: "≈ 2.2 × 10⁻¹⁸ s⁻¹" },
      { symbol: "d", name: "Distance to galaxy", unit: "m or Mpc" },
    ],
    notes: "Recession speed is proportional to distance. Age of universe d/v ≈ 1/H₀ (approx. 13.8 billion years).",
  },
];
